import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

interface FaqItem {
  q: string;
  a: string;
}

interface FaqSection {
  category: string;
  items: FaqItem[];
}

/* Grounded in the implemented product — keep in sync with the user guide. */
const FAQ_SECTIONS: FaqSection[] = [
  {
    category: "시작하기",
    items: [
      {
        q: "서비스를 시작하려면 무엇이 필요한가요?",
        a: "운영자 콘솔에 로그인한 뒤 숙소 → 객실 타입 → 호실 → 가이드·FAQ 순서로 등록하면 준비가 끝납니다. 예약을 만들면 게스트 링크가 자동 발급되어 바로 응대를 시작할 수 있습니다. 자세한 순서는 사용 가이드를 참고하세요.",
      },
      {
        q: "로그인은 어떻게 하나요?",
        a: "Google 계정 로그인(Firebase 활성 환경)을 사용합니다. 허용된 운영자 이메일만 접근할 수 있으며, 화이트리스트에 포함된 이메일로 처음 로그인하면 계정이 자동 생성됩니다. Firebase가 비활성화된 환경에서는 이메일·비밀번호 로그인과 첫 관리자 등록이 제공됩니다.",
      },
      {
        q: "숙소를 여러 개 운영하는데 계정을 여러 개 만들어야 하나요?",
        a: "아니요. 계정 하나로 숙소를 필요한 만큼 등록할 수 있습니다. 사이드바 상단의 숙소 선택기에서 대상 숙소를 고르면 예약·호실·객실 타입·가이드·리포트가 모두 그 숙소 기준으로 표시됩니다.",
      },
    ],
  },
  {
    category: "게스트 이용",
    items: [
      {
        q: "게스트가 앱을 설치하거나 회원가입을 해야 하나요?",
        a: "아니요. 게스트는 전달받은 전용 링크(/g/…)를 브라우저에서 여는 것만으로 챗봇 컨시어지를 사용할 수 있습니다. 앱 설치·회원가입·로그인이 전혀 필요 없습니다.",
      },
      {
        q: "어떤 언어를 지원하나요?",
        a: "한국어 · English · 中文 · 日本語 4개 언어를 지원합니다. 게스트가 처음 접속할 때 언어를 고르면 환영 문구·빠른 질문 버튼·챗봇 답변이 그 언어로 자동 번역되고, 선택은 다음 방문에도 유지됩니다. 채팅 화면 우측 상단에서 언제든 바꿀 수 있습니다.",
      },
      {
        q: "챗봇은 무엇을 근거로 답하나요? 아무 말이나 지어내지 않나요?",
        a: "답변은 숙소 정보, 연결된 호실 정보, 게시된 가이드·FAQ, 예약 정보만 근거로 생성됩니다. 등록되지 않은 내용은 추측하지 않고 프런트 문의를 안내하므로, 콘텐츠를 풍부하게 등록할수록 답변이 정확해집니다.",
      },
      {
        q: '게스트가 "링크가 안 열려요"라고 하면 어떻게 하나요?',
        a: '만료되었거나 잘못된 링크, 또는 예약 취소로 비활성화된 링크로 접속하면 "유효하지 않은 링크" 화면이 표시됩니다. 예약·프런트 화면의 해당 예약 행에서 챗봇 링크 발급 또는 챗봇 링크 복사로 유효한 링크를 다시 전달해 주세요.',
      },
      {
        q: "대화 기록은 유지되나요? 운영자도 볼 수 있나요?",
        a: "네. 게스트가 링크를 다시 열어도 대화 기록이 그대로 유지됩니다. 운영자는 예약 행의 대화 보기에서 전체 대화를 읽기 전용으로 열람할 수 있습니다.",
      },
    ],
  },
  {
    category: "예약 · 프런트",
    items: [
      {
        q: "게스트는 어떻게 등록하나요?",
        a: "게스트를 따로 등록하는 화면은 없습니다. 예약을 만들면 그 순간 게스트와 전용 링크가 자동 발급됩니다. 콘솔에서 직접 만든 예약과 OTA로 들어온 예약 모두 동일합니다.",
      },
      {
        q: "예약 금액은 어떻게 계산되나요?",
        a: "객실 타입의 1박 기본요금 × 숙박 일수로 자동 계산됩니다. 예약은 해당 기간에 가용 객실이 있을 때만 생성됩니다.",
      },
      {
        q: "체크인 처리를 하면 무슨 일이 일어나나요?",
        a: "체크인 시 해당 타입의 빈 호실 중에서 실제 호실을 배정합니다. 배정된 호실의 와이파이·출입 안내가 게스트 챗봇 답변에 반영됩니다. 체크아웃·취소도 예약 행에서 바로 처리합니다.",
      },
      {
        q: "예약을 취소하면 게스트 링크는 어떻게 되나요?",
        a: "예약을 취소하면 해당 게스트의 링크도 함께 비활성화됩니다. OTA에서 취소된 예약도 동일하게 처리됩니다.",
      },
    ],
  },
  {
    category: "채널 · OTA 연동",
    items: [
      {
        q: "어떤 OTA와 연동되나요?",
        a: "채널 매니저를 통해 Booking.com 등 여러 OTA와 하나의 연동으로 연결됩니다. OTA 채널의 연결·매핑 자체는 운영팀이 설정하고, 콘솔의 채널 화면에서 연동 상태와 숙소별 매핑 현황을 확인합니다.",
      },
      {
        q: "요금이나 재고를 바꾸면 OTA에 언제 반영되나요?",
        a: "숙소·객실 타입·요금·재고는 등록·수정·예약이 일어날 때마다 자동으로 동기화되고, 매일 새벽 전체 동기화도 한 번 돌아갑니다. 상태가 어긋나 보일 때만 채널 화면의 전체 동기화 버튼을 수동 실행하면 됩니다.",
      },
      {
        q: "OTA 예약이 들어오면 따로 처리해야 하나요?",
        a: "아니요. OTA 예약은 예약 목록에 channex:<OTA명> 채널로 자동 등록되고, 게스트와 챗봇 링크도 자동 생성됩니다. OTA가 전달한 이메일·전화번호·언어까지 채워집니다. 단, OTA 예약의 취소·변경은 해당 OTA에서 해야 하며 콘솔에서는 할 수 없습니다.",
      },
      {
        q: "만실인데 OTA 예약이 들어오면 어떻게 되나요?",
        a: "OTA 예약은 놓치지 않는 것이 우선이라 만실이어도 일단 접수됩니다. 오버부킹이 발생하면 예약·프런트 화면에서 직접 정리해 주세요.",
      },
      {
        q: "호실 이름(501호 등)도 OTA에 노출되나요?",
        a: "아니요. 채널 매니저에는 객실 타입 단위(타입별 객실 수·요금·재고)로만 동기화됩니다. 개별 호실은 PMS 내부에서만 관리되며, 호실을 추가·삭제하면 해당 타입의 재고 수만 자동 갱신됩니다.",
      },
    ],
  },
  {
    category: "데이터 · 기타",
    items: [
      {
        q: "리포트에서는 무엇을 볼 수 있나요?",
        a: "조회 기간을 지정해 점유율(점유 객실-박 ÷ 전체 객실 × 기간 일수), 총 매출(비취소 예약 금액 합계), 예약 채널 수, 채널별 매출 비교를 확인할 수 있습니다.",
      },
      {
        q: "숙소나 호실이 삭제되지 않아요.",
        a: "데이터 보호를 위한 제약입니다. 게스트가 연결된 호실은 연결을 해제해야 삭제할 수 있고, 객실 타입·예약이 남아 있는 숙소는 하위 데이터를 먼저 정리해야 삭제할 수 있습니다.",
      },
      {
        q: "챗봇 답변 품질을 높이려면 어떻게 해야 하나요?",
        a: "가이드·FAQ 화면에서 체크인·와이파이·주차·주변 정보처럼 자주 묻는 항목부터 마크다운으로 등록하고 게시 상태를 켜세요. 자주 쓰는 항목은 빠른 질문 버튼으로 노출하면 게스트가 탭 한 번으로 안내받을 수 있습니다.",
      },
    ],
  },
];

/** Public FAQ — native disclosure widgets grouped by category. */
export function FaqPage() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 md:py-16">
      <header className="animate-rise max-w-2xl">
        <p className="eyebrow-brass">FAQ</p>
        <h1 className="mt-3 break-keep font-display text-3xl font-medium tracking-tight text-foreground sm:text-4xl">
          자주 묻는 질문
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          현재 제공 중인 기능 기준으로 정리했습니다. 더 자세한 설정 방법은{" "}
          <Link to="/guide" className="font-medium text-primary underline underline-offset-2">
            사용 가이드
          </Link>
          에서 화면과 함께 확인할 수 있습니다.
        </p>
      </header>

      <div className="mt-10 space-y-10">
        {FAQ_SECTIONS.map((section) => (
          <section key={section.category} className="animate-rise">
            <h2 className="eyebrow mb-3">{section.category}</h2>
            <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-card">
              {section.items.map((item) => (
                <details key={item.q} className="group">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-[15px] font-medium text-foreground transition-colors hover:bg-secondary/50 [&::-webkit-details-marker]:hidden">
                    {item.q}
                    <ChevronDown
                      className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
                      aria-hidden
                    />
                  </summary>
                  <p className="border-t border-border/40 bg-secondary/30 px-5 py-4 text-sm leading-relaxed text-muted-foreground">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-14 rounded-2xl border border-border/60 bg-card p-6 text-center shadow-card sm:p-8">
        <h2 className="font-display text-xl font-medium tracking-tight text-foreground">
          원하는 답을 찾지 못하셨나요?
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          사용 가이드에서 전체 설정 과정을 실제 화면과 함께 볼 수 있습니다.
        </p>
        <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
          <Button asChild>
            <Link to="/guide">사용 가이드 보기</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/admin/login">콘솔 로그인</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
